import { useCallback, useEffect, useSyncExternalStore } from 'react'
import { CALL_PROVIDER_CHANGED_EVENT, CallProvider, fetchCallProviderFromExtension, getStoredCallProvider, saveCallProvider } from '../utils/callProvider'

/**
 * Call-provider preference shared by every call action on the LoadHub page.
 * Persisted in localStorage; changes broadcast via a window event. Backed by a
 * module-level store so any number of consumers (every table row mounts this)
 * share ONE window listener and one snapshot instead of per-mount listeners.
 * On mount, adopts the extension's saved option (when installed) so both dial
 * paths stay aligned.
 */

let snapshot: CallProvider | null = null
const storeListeners = new Set<() => void>()

const handleProviderChanged = (event: Event) => {
    snapshot = (event as CustomEvent<CallProvider>).detail
    storeListeners.forEach((listener) => listener())
}

const subscribe = (listener: () => void) => {
    if (storeListeners.size === 0) window.addEventListener(CALL_PROVIDER_CHANGED_EVENT, handleProviderChanged)
    storeListeners.add(listener)
    return () => {
        storeListeners.delete(listener)
        if (storeListeners.size === 0) window.removeEventListener(CALL_PROVIDER_CHANGED_EVENT, handleProviderChanged)
    }
}

const getSnapshot = (): CallProvider => {
    if (snapshot === null) snapshot = getStoredCallProvider()
    return snapshot
}

// Extension sync state. 'done' only after a SUCCESSFUL (non-null) read — a null result (extension
// absent, still loading, or an old version without the handler) goes back to 'idle' so a later
// mount retries instead of leaving the page permanently unsynced. 'pending' just prevents
// concurrent pings while one is in flight.
let extensionSyncState: 'idle' | 'pending' | 'done' = 'idle'

const syncFromExtension = () => {
    if (extensionSyncState !== 'idle') return
    extensionSyncState = 'pending'
    fetchCallProviderFromExtension()
        .then((extensionProvider) => {
            if (extensionProvider) {
                extensionSyncState = 'done'
                if (extensionProvider !== getStoredCallProvider()) saveCallProvider(extensionProvider)
            } else {
                extensionSyncState = 'idle'
            }
        })
        .catch((error) => {
            extensionSyncState = 'idle'
            console.error('Call provider extension sync failed:', error)
        })
}

export const useCallProvider = (): { provider: CallProvider; setProvider: (next: CallProvider) => void } => {
    const provider = useSyncExternalStore(subscribe, getSnapshot)

    useEffect(() => {
        syncFromExtension()
    }, [])

    const setProvider = useCallback((next: CallProvider) => saveCallProvider(next), [])

    return { provider, setProvider }
}
